import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Label,
  Tooltip,
  ReferenceDot,
  Customized,
  ReferenceLine,
  XAxisProps,
  YAxisProps,
} from "recharts";

import {
  MutableRefObject,
  useContext,
  useMemo,
  useState,
} from "react";
import "./depth-graph.css";

import { arrayEquals } from "src/utils/arrayUtils";
import { DataContext, defaultCoarseSampleRate } from "src/contexts/DataContext";
import { SAFE_getCmap, cmaps } from "src/cmaps/cmaps";
import {
  useDepthChartInteractions,
  usePlayheadInteractions,
} from "./depth-graph.interactions";
import {
  SharedStateEvent,
  useSharedStateListener,
} from "src/utils/shared-state";
import dayjs from "dayjs";
import { useChannelData } from "src/contexts/EDFDataContext";

function rescaleColor(rgb_array) {
  return `rgb(${rgb_array?.[0] || 0},${rgb_array?.[1] || 0},${
    rgb_array?.[2] || 0
  })`;
}

function dataToGradient(data, selectedVar) {
  const EPSILON = 0.0000001;
  const cmap = SAFE_getCmap(data, selectedVar);
  const colors = data.map((d) => {
    return cmap[d[selectedVar]];
  });
  let colorBreaks = [];
  colorBreaks.push({
    break: 0,
    color: rescaleColor(colors[0]),
  });
  for (let i = 1; i < colors.length - 1; i++) {
    if (!arrayEquals(colors[i - 1], colors[i])) {
      colorBreaks.push({
        break: i / colors.length - EPSILON,
        color: rescaleColor(colors[i]),
      });
      colorBreaks.push({
        break: i / colors.length,
        color: rescaleColor(colors[i]),
      });
    }
    if (!arrayEquals(colors[i + 1], colors[i])) {
      colorBreaks.push({
        break: i / colors.length,
        color: rescaleColor(colors[i]),
      });
      colorBreaks.push({
        break: i / colors.length + EPSILON,
        color: rescaleColor(colors[i]),
      });
    }
  }
  colorBreaks.push({
    break: 1,
    color: rescaleColor(colors.at(colors.length - 1)),
  });

  return (
    <defs>
      <linearGradient id="colorUv" x1="0" y1="0" x2="100%" y2="0">
        <stop offset="0%" stopColor={rescaleColor(colors[0])} />
        {colorBreaks.map((c) => (
          <stop offset={`${c.break * 100}%`} stopColor={c.color} />
        ))}
        <stop
          offset="100%"
          stopColor={colorBreaks[colorBreaks.length - 1]?.color}
        />
      </linearGradient>
    </defs>
  );
}

interface PlayheadProps {
  xAxisMap: XAxisProps;
  yAxisMap: YAxisProps;
  playheadPressRef: MutableRefObject<boolean>;
  data: any;
  onMouseEnter: React.MouseEvent<SVGGElement, MouseEvent>;
  onMouseLeave: React.MouseEvent<SVGGElement, MouseEvent>;
}

const Playhead = (props: PlayheadProps) => {
  const {
    xAxisMap,
    yAxisMap,
    playheadPressRef,
    data: graphData,
    onMouseEnter,
    onMouseLeave,
  } = props;
  const { data, dataWindow } = useContext(DataContext);
  const [idx, setIdx] = useState(window.sharedState.idx);

  const { onMouseDown } = usePlayheadInteractions({
    playheadPressRef,
    dataWindow,
    graphData,
    allData: data,
    idx,
  });

  useSharedStateListener(SharedStateEvent.IDX_UPDATE, (state) => {
    setIdx(state.idx);
  });

  const x = data[idx]?.time;
  const y = -data[idx]?.Depth;

  const topTick = yAxisMap[0].niceTicks?.[yAxisMap[0].niceTicks?.length - 1];

  const eventHandlers: any = {
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
  };

  return (
    <>
      <ReferenceDot
        x={x}
        y={y}
        r={8}
        className="playhead"
        fillOpacity={0.4}
        stroke="white"
        strokeWidth={1}
        xAxis={xAxisMap[0]}
        yAxis={yAxisMap[0]}
        isFront
        {...eventHandlers}
      />
      <ReferenceLine
        x={x}
        className="playhead-line"
        stroke="white"
        strokeOpacity={0.5}
        strokeWidth={1}
        {...eventHandlers}
      />
      <ReferenceLine
        x={x - 3}
        className="playhead-line"
        stroke="white"
        strokeOpacity={0}
        strokeWidth={6}
        {...eventHandlers}
      />
      <ReferenceDot
        className="playhead-area"
        x={x}
        y={topTick}
        xAxis={xAxisMap[0]}
        yAxis={yAxisMap[0]}
        shape={(props) => (
          <>
            <rect
              x={props.cx - 12}
              y={props.cy - 6}
              width="24"
              height="12"
              rx="5"
              ry="5"
              fill="#ddd"
              fillOpacity={0.9}
              stroke="#aaa"
              stroke-width="1"
              {...eventHandlers}
            />
            <line
              x1={props.cx - 3}
              y1={props.cy - 3}
              x2={props.cx - 3}
              y2={props.cy + 3}
              stroke="#999"
              stroke-width="1"
              {...eventHandlers}
            />
            <line
              x1={props.cx}
              y1={props.cy - 3}
              x2={props.cx}
              y2={props.cy + 3}
              stroke="#999"
              stroke-width="1"
              {...eventHandlers}
            />
            <line
              x1={props.cx + 3}
              y1={props.cy - 3}
              x2={props.cx + 3}
              y2={props.cy + 3}
              stroke="#999"
              stroke-width="1"
              cursor="grab"
              {...eventHandlers}
            />
          </>
        )}
      />
    </>
  );
};

export const DepthChart = () => {
  const [showTooltip, setShowTooltip] = useState(true);
  const {
    data: _data,
    window: timeWindow,
    selectedVar,
    dataWindow,
  } = useContext(DataContext);

  const { data: channelData } = useChannelData();

  const data = useMemo(
    () =>
      _data.filter((d, idx) => {
        let timeMatch = true;
        if (dataWindow.length === 2) {
          timeMatch = d.R_Time >= dataWindow[0] && d.R_Time <= dataWindow[1];
        }
        const sampleRate = d["sample_rate"];
        const correctionFactor = Math.floor(
          defaultCoarseSampleRate / sampleRate
        );
        const correctionMatch = idx % correctionFactor === 0;
        return timeMatch && correctionMatch;
      }),
    [_data, dataWindow]
  );

  const filteredData = useMemo(() => {
    if (!timeWindow.length) return data;
    const startTime = timeWindow[0];
    const endTime = timeWindow[1];
    return data.filter((d) => d.R_Time > startTime && d.R_Time < endTime);
  }, [data, timeWindow]);

  const {
    setScrollListener,
    removeScrollListener,
    handleClick,
    onHover,
    localWindowSelection,
    playheadPress,
  } = useDepthChartInteractions({
    data: _data,
    filteredData,
  });

  const displayDays = useMemo(
    () =>
      dayjs(filteredData[filteredData.length - 1].time).diff(
        dayjs(filteredData[0].time),
        "day"
      ) > 1,
    [filteredData]
  );

  const displayMinutes = useMemo(
    () =>
      dayjs(filteredData[filteredData.length - 1].time).diff(
        dayjs(filteredData[0].time),
        "hours"
      ) < 2,
    [filteredData]
  );

  const xTicks = useMemo(() => {
    
    const ticks = [];
    if (displayDays) {
      const dayCount = dayjs(filteredData[filteredData.length - 1].time).diff(
        dayjs(filteredData[0].time),
        "day"
      );
      for (let i = 1; i < dayCount + 2; i++) {
        ticks.push(
          dayjs(filteredData[0].time).add(i, "day").startOf("day").valueOf()
        );
      }
    } else if (displayMinutes) {
      const minuteCount = dayjs(filteredData[filteredData.length - 1].time).diff(
        dayjs(filteredData[0].time),
        "minute"
      );
      for (let i = 1; i < minuteCount + 2; i++) {
        ticks.push(
          dayjs(filteredData[0].time).add(i, "minute").startOf("minute").valueOf()
        );
      }
    } else {
      const hourCount = dayjs(filteredData[filteredData.length - 1].time).diff(
        dayjs(filteredData[0].time),
        "hour"
      );
      for (let i = 1; i < hourCount + 1; i++) {
        ticks.push(
          dayjs(filteredData[0].time).add(i, "hour").startOf("hour").valueOf()
        );
      }
    }

    return ticks;
  }, [displayDays, displayMinutes, filteredData]);

  if (!data || data.length === 0) return;

  const fontAttribs = { fill: "white" };

  return (
    <div
      className="stats-panel"
      onMouseEnter={setScrollListener}
      onMouseLeave={removeScrollListener}
    >
      <ResponsiveContainer width="100%" height={180}>
        <LineChart
          margin={{
            top: 15,
            right: 15,
            left: 15,
            bottom: 10,
          }}
          data={filteredData}
          style={fontAttribs}
          onClick={handleClick}
          onMouseMove={onHover}
        >
          {dataToGradient(filteredData, selectedVar)}
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tick={fontAttribs}
            tickFormatter={(value) =>
              displayDays
                ? dayjs(value).format("YYYY-MM-DD")
                : displayMinutes
                ? dayjs(value).format("MMM DD HH:mm")
                : dayjs(value).format("MMM DD Ha")
            }
            ticks={xTicks}
            type="number"
            domain={[
              filteredData[0].time - 10000,
              filteredData[filteredData.length - 1].time + 10000,
            ]}
          />
          <YAxis dataKey={(d) => -d.Depth} tick={fontAttribs}>
            <Label
              style={{
                textAnchor: "middle",
                fontSize: "130%",
                fill: "white",
              }}
              position="left"
              angle={270}
              value={"Depth (m)"}
            />
          </YAxis>
          <Tooltip
            itemStyle={{ color: "black" }}
            wrapperStyle={{
              color: "black",
              opacity: showTooltip ? 1 : 0,
              transition: "opacity 0.2s",
            }}
            labelFormatter={(value) =>
              dayjs(value).format("YYYY-MM-DD HH:mm:ss")
            }
            formatter={(value: number) => ["Depth: " + value.toFixed(2) + " m"]}
          />
          <Line
            type="monotone"
            dataKey={(d) => -d.Depth}
            fillOpacity={1}
            strokeWidth={displayDays ? 1 : 2}
            stroke={
              displayDays
                ? rescaleColor(cmaps["Sleep_Code"]["Active Waking"])
                : "url(#colorUv)"
            }
            dot={false}
            name="x"
          />
          {channelData?.timespan && (
            <ReferenceArea
              x1={channelData.timespan[0]}
              y1={-1000}
              x2={channelData.timespan[1]}
              y2={1000}
              fill="rgba(255, 192, 203, 0.5)"
              ifOverflow="visible"
            />
          )}
          {localWindowSelection[0] && localWindowSelection[1] && (
            <ReferenceArea
              x1={filteredData?.[localWindowSelection[0]]?.time}
              y1={-1000}
              x2={filteredData?.[localWindowSelection[1]]?.time}
              y2={1000}
              fill="PowderBlue"
              ifOverflow="visible"
            />
          )}
          <Customized
            component={Playhead as any}
            playheadPressRef={playheadPress}
            yAxisDomain={["auto", "auto"]}
            onMouseEnter={() => setShowTooltip(false)}
            onMouseLeave={() => setShowTooltip(true)}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
